/**
 * ReportsVerifyPanel — Owner-only "Reporting Freeze" status panel.
 *
 * Fires the Owner-side regression run (POST /api/owner/reports/verify-frozen)
 * which executes the 6 frozen-report fingerprints against the live system
 * and returns a structured pass/fail summary. Lets the Owner sanity-check
 * before any prospect demo or production deploy without dropping to the
 * CLI.
 */
import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Play, CheckCircle2, XCircle, AlertTriangle, Loader2, Clock } from "lucide-react";

const STATUS_ICON = {
  pass: <CheckCircle2 className="h-4 w-4 text-emerald-600" />,
  fail: <XCircle className="h-4 w-4 text-red-600" />,
  error: <AlertTriangle className="h-4 w-4 text-red-600" />,
  "missing-baseline": <AlertTriangle className="h-4 w-4 text-amber-500" />,
};

const STATUS_LABEL = {
  pass: "PASS",
  fail: "REGRESSION",
  error: "ERROR",
  "missing-baseline": "NO BASELINE",
};

const STATUS_TONE = {
  pass: "text-emerald-700",
  fail: "text-red-700",
  error: "text-red-700",
  "missing-baseline": "text-amber-700",
};

function fmtMs(ms) {
  if (!ms && ms !== 0) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export function ReportsVerifyPanel() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [status, setStatus] = useState(null);  // nightly streak status

  const loadStatus = async () => {
    try {
      const r = await api.get("/owner/reports/freeze-status");
      setStatus(r.data);
    } catch (_e) {
      // Non-fatal — the panel still works without the streak pill.
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const run = async () => {
    setRunning(true);
    setErr(null);
    setResult(null);
    try {
      const r = await api.post("/owner/reports/verify-frozen");
      setResult(r.data);
      loadStatus();
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Verification failed");
    } finally {
      setRunning(false);
    }
  };

  const runNightlyNow = async () => {
    setRunning(true);
    setErr(null);
    try {
      await api.post("/owner/reports/run-nightly-now");
      toast.success("Nightly check executed — streak updated.");
      loadStatus();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Run failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <section
      className="mt-6 rounded-2xl border border-black/5 bg-white p-6"
      data-testid="reports-verify-panel"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="mt-1 flex h-9 w-9 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
            <ShieldCheck className="h-4 w-4" />
          </span>
          <div>
            <h2 className="font-display text-lg font-semibold text-ek-ink">
              Reporting freeze — verify
            </h2>
            <p className="mt-1 text-sm text-ek-ink/70">
              Runs the 6 frozen-report fingerprints against the live system
              and reports any regression. Safe to fire before any prospect
              demo, customer delivery, or production deploy.{" "}
              <span className="font-medium text-ek-ink">
                Audit-logged.
              </span>
            </p>
            <StreakPill status={status} />
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <button
            type="button"
            onClick={run}
            disabled={running}
            className="ek-btn inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="reports-verify-run"
          >
            {running ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            {running ? "Running…" : "Run verification"}
          </button>
          <button
            type="button"
            onClick={runNightlyNow}
            disabled={running}
            className="text-[11px] font-semibold text-ek-ink/60 hover:text-ek-purple disabled:opacity-50"
            data-testid="reports-verify-run-nightly"
            title="Same code path as the 03:30 UTC scheduled job. Updates the streak pill."
          >
            Run nightly check now
          </button>
        </div>
      </div>

      {err && (
        <p className="mt-4 text-sm text-red-600" data-testid="reports-verify-error">
          {err}
        </p>
      )}

      {result && (
        <div className="mt-5" data-testid="reports-verify-result">
          <div
            className={`rounded-xl border p-3 ${
              result.ok
                ? "border-emerald-200 bg-emerald-50"
                : "border-red-200 bg-red-50"
            }`}
            data-testid="reports-verify-summary"
          >
            <div className="flex items-center gap-2">
              {result.ok ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-700" />
              ) : (
                <XCircle className="h-5 w-5 text-red-700" />
              )}
              <span
                className={`text-sm font-semibold ${
                  result.ok ? "text-emerald-700" : "text-red-700"
                }`}
              >
                {result.ok
                  ? `All ${result.summary.passed} reports verified`
                  : `${result.summary.failed} report${
                      result.summary.failed === 1 ? "" : "s"
                    } regressed — DO NOT DEPLOY`}
              </span>
            </div>
            <p className="mt-1 text-xs text-ek-ink/60">
              Ran at{" "}
              <code>{new Date(result.ran_at).toLocaleString("en-GB")}</code> ·
              {" "}
              {result.summary.passed}/{result.summary.total} pass
              {result.summary.missing_baseline > 0 &&
                ` · ${result.summary.missing_baseline} missing baseline`}
            </p>
          </div>

          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="reports-verify-table">
              <thead>
                <tr className="border-b border-black/5 text-xs uppercase tracking-wide text-ek-ink/60">
                  <th className="py-2 pr-3 font-medium">Report</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 pr-3 font-medium">Body drift</th>
                  <th className="py-2 pr-3 font-medium">Time</th>
                  <th className="py-2 pr-3 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody>
                {result.results.map((row) => (
                  <tr
                    key={row.slug}
                    className="border-b border-black/5 last:border-0 align-top"
                    data-testid={`reports-verify-row-${row.slug}`}
                  >
                    <td className="py-2 pr-3 font-medium text-ek-ink">{row.slug}</td>
                    <td className="py-2 pr-3">
                      <div
                        className={`flex items-center gap-1.5 text-xs font-semibold ${STATUS_TONE[row.status] || "text-ek-ink"}`}
                        data-testid={`reports-verify-status-${row.slug}`}
                      >
                        {STATUS_ICON[row.status]}
                        {STATUS_LABEL[row.status] || row.status}
                      </div>
                    </td>
                    <td className="py-2 pr-3 tabular-nums text-xs text-ek-ink/70">
                      {row.body_drift_pct != null ? `${row.body_drift_pct}%` : "—"}
                    </td>
                    <td className="py-2 pr-3 tabular-nums text-xs text-ek-ink/70">
                      {fmtMs(row.duration_ms)}
                    </td>
                    <td className="py-2 pr-3 text-xs text-ek-ink/75">
                      <div className="whitespace-pre-wrap break-words max-w-md">
                        {row.message}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

export default ReportsVerifyPanel;

function StreakPill({ status }) {
  if (!status) return null;
  if (!status.has_runs) {
    return (
      <div
        className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700"
        data-testid="reports-freeze-streak-pill"
      >
        <Clock className="h-3 w-3" />
        Nightly check has not run yet · first run at{" "}
        {new Date(status.next_run_at_utc).toLocaleString("en-GB")}
      </div>
    );
  }
  const latest = status.latest;
  const ok = latest?.ok;
  const tone = ok
    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
    : "border-red-200 bg-red-50 text-red-700";
  const Icon = ok ? CheckCircle2 : XCircle;
  const label = ok
    ? status.streak >= 7
      ? `Green for ${status.streak} consecutive nights ✓`
      : `Green for ${status.streak} night${status.streak === 1 ? "" : "s"}`
    : "Last nightly run FAILED — investigate before deploy";
  return (
    <div
      className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${tone}`}
      data-testid="reports-freeze-streak-pill"
    >
      <Icon className="h-3 w-3" />
      {label}
      <span className="text-ek-ink/45 font-normal">
        · last run{" "}
        {new Date(latest.started_at).toLocaleString("en-GB", {
          day: "2-digit",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
        })}
      </span>
    </div>
  );
}
